import { GetStaticPropsContext, PreviewData } from 'next/types';
import { ParsedUrlQuery } from 'querystring';
import { useI18n } from 'next-localization';
import { NextRouter } from 'next/router';
import { ComponentRendering, ImageField, PlaceholdersData } from '@sitecore-jss/sitecore-jss-nextjs';
import * as crypto from 'crypto';
import DOMPurify from 'dompurify';
import truncate from 'html-truncate';
import Cookies from 'js-cookie';
import { buildEvolveURL } from 'components/PlanData/utilities';

export function sanitizeAndTruncate(html: string, maxLength: number) {
  const sanitizeHtml = DOMPurify.sanitize(html, { ALLOWED_TAGS: ['em'] });
  const sanitizeAndtruncateHtml = truncate(sanitizeHtml, maxLength, { ellipsis: '...' });
  return sanitizeAndtruncateHtml;
}

export function getQueryStringParameter(param: string, query: ParsedUrlQuery): string {
  if (query == null || query === undefined || param == null || param === undefined) {
    return '';
  }

  for (const queryParam of Object.keys(query)) {
    if (queryParam.toLowerCase() === param.toLowerCase()) {
      const value = query[queryParam];
      return Array.isArray(value) ? value[0] : !value ? '' : value;
    }
  }

  return '';
}

export function addQueryStringParameter(url: string, key: string, value: string): string {
  if (!url || !key || !value) {
    return url;
  }

  const urlHasSearchParams = url.indexOf('?') > -1;
  url += `${urlHasSearchParams ? '&' : '?'}${key}=${value}`;

  return url;
}

export function isEmpty(obj: object) {
  for (const prop in obj) {
    if (Object.hasOwn(obj, prop)) {
      return false;
    }
  }

  return true;
}

export function isNullOrEmptyObject(obj: object) {
  if (obj == null || obj === undefined) {
    // obj is null or undefined
    return true;
  }

  if (typeof obj !== 'object') {
    // boolean, number, string, function, etc.
    return false;
  }

  const proto = Object.getPrototypeOf(obj);

  // consider `Object.create(null)`, commonly used as a safe map
  // before `Map` support, an empty object as well as `{}`
  if (proto !== null && proto !== Object.prototype) {
    return false;
  }

  return isEmpty(obj);
}

/**
 * This function takes in a string and replaces any opening and closing HTML tags
 * with an empty string.
 * @param str The input string you are checking for HTML tags.
 * @returns a copy of the input string with any HTML tags removed.
 */
export function removeTags(str: string): string {
  if (str === null || str === '') return str;
  else str = str.toString();

  // Regular expression to identify HTML tags in
  // the input string. Replacing the identified
  // HTML tag with a null string.
  return str.replace(/(<([^>]+)>)/gi, '');
}

export function getSlugsFromPageContext(
  context: GetStaticPropsContext<ParsedUrlQuery, PreviewData>,
  keepSiteInSlug = true
): string[] {
  const pathParts = context.params ? (context.params['path'] as string[]) : [];
  if (!pathParts.length) return [];

  if (context.locale?.length) {
    pathParts.unshift(context.locale);
  }

  if (!keepSiteInSlug) {
    return pathParts.filter((path, index) => {
      // first slug contains the multisite part "_site_upmchealthplan" that needs to be stripped out when keepSiteInSlug = false
      if (index === 0 && path.includes('_site_')) return false;
      return true;
    });
  }

  return pathParts;
}

/**
 * This function takes in a string and removes any line-breaks. Line-breaks in Richtext field was causing in issue in rendering of the component
 * Bug# 614394
 * @param str The input string.
 * @returns a copy of the input string with any line-breaks removed.
 */
export function removeLineBreaks(str: string): string {
  if (str === null || str === '') {
    return str;
  }

  str = str.replace(/['\r\n']/g, '');
  str = str.replace(/['\n']/g, '');
  str = str.replace(/['\r']/g, '');
  str = str.replace(/&#160;/g, '\u00A0');

  return str;
}

export function useDictionary(key: string, defaultValue = ''): string {
  const { t } = useI18n();

  return t(key) || defaultValue || key;
}

function isRelativeLink(url: string): boolean {
  const isSameDomainUrl = url.startsWith(document.location.origin);

  return isSameDomainUrl || (url?.startsWith('/') && !url?.startsWith('/#') && !url?.startsWith('//'));
}

function linkShowsWarningModal(
  url: string,
  whiteListDomains: string,
  link: HTMLAnchorElement,
  excludeType = '',
  whiteListRoutes = ''
): boolean {
  const isSpecialLink = (url: string): boolean =>
    (excludeType || '').length > 0 ||
    url.startsWith('tel:') ||
    url.startsWith('mailto:') ||
    url.startsWith('javascript:') ||
    isHeaderLink(link);

  // For special links - always return false - no warning
  const ignoreSpecialLink = isSpecialLink(url);

  if (ignoreSpecialLink || !url) {
    return false;
  }

  const $relativeLink = isRelativeLink(url);

  // Handle special case for internal links
  if ($relativeLink) {
    // For internal links, if no white-listing is defined or * - then do not show warning for any internal links
    if (!whiteListRoutes || whiteListRoutes === '*') {
      return false;
    }

    const $urlPageRoute = url.replace(document.location.origin, '');

    // This is the case when internal routes white-listing is defined, so check if the current route is in the white-listing or not
    const $routes = whiteListRoutes.toLowerCase().split(',');
    let $isWhiteListedRoute = false;

    // Routes white-listing is defined, so check if the current route is in the white-listing or not
    $routes.forEach(($route) => {
      $route = $route.toLowerCase().trim();

      if ($urlPageRoute.toLowerCase().indexOf($route) >= 0) {
        $isWhiteListedRoute = true;
      }
    });

    return !$isWhiteListedRoute;
  }

  // Handle special case for external links
  if (!$relativeLink && whiteListDomains) {
    // If * is added to white-listing for domains, allow all external links without warning
    if (whiteListDomains === '*') {
      return false;
    }

    // Check if the current link is white-listed or not?
    const $domains = whiteListDomains.toLowerCase().split(',');
    let $isWhiteListedDomain = false;

    $domains.forEach(($domain) => {
      $domain = $domain.toLowerCase().trim();

      if (url.toLowerCase().indexOf($domain) >= 0) {
        $isWhiteListedDomain = true;
      }
    });

    return !$isWhiteListedDomain;
  }

  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('//')) {
    return true;
  }

  return false;
}

export function findLinkTag(element: HTMLElement): HTMLElement {
  while (element && element.tagName !== 'A') {
    element = element.parentNode as HTMLElement;
  }

  return element;
}

export function handleExternalLinkClick(
  event: MouseEvent,
  whiteListDomains: string,
  externalLinkCallback: (url: string, target?: string | null) => void,
  internalLinkCallback: (url: string, target?: string | null) => void,
  whiteListRoutes: string
) {
  let target = event.target as HTMLElement;

  if (target && target.tagName !== 'A') {
    target = findLinkTag(target);
  }

  if (target) {
    const { tagName } = target;

    if (tagName === 'A') {
      const href = target.getAttribute('data-href') || target.getAttribute('href');
      const linkTarget = target.getAttribute('target');
      const excludeType = target.getAttribute('data-exclude-type') || '';

      if (href && !href.startsWith('#') && !excludeType) {
        if (linkShowsWarningModal(href, whiteListDomains, target as HTMLAnchorElement, excludeType, whiteListRoutes)) {
          stopEventPropagation(event);
          externalLinkCallback && externalLinkCallback(href, linkTarget);
        } else {
          stopEventPropagation(event);
          internalLinkCallback && internalLinkCallback(href, linkTarget);
        }
      }
    }
  }
}

export function enableScrolling($enable = true, $tag = 'html'): void {
  const $elements = document.getElementsByTagName($tag);
  const $element = $elements ? $elements[0] : undefined;

  if ($element) {
    $enable ? $element.classList.remove('no-scroll') : $element.classList.add('no-scroll');
  }
}

export function stopEventPropagation(event: MouseEvent) {
  event.stopPropagation();
  event.stopImmediatePropagation();
  event.preventDefault();
}

function isHeaderLink(link: HTMLAnchorElement) {
  if (!link) {
    return false;
  }

  const header = document.getElementById('header-container');
  const headerLinks = header?.querySelectorAll('a');
  const headerLinksArray = headerLinks ? Array.from(headerLinks) : [];

  return headerLinksArray.length ? headerLinksArray.indexOf(link) >= 0 : false;
}

export const setNodeProperty = (node: Node, property: string, propertyValue: string) => {
  const descriptor = Object.getOwnPropertyDescriptor(node, property);
  if (descriptor?.configurable) {
    Object.defineProperty(node, property, {
      value: propertyValue,
      writable: false,
    });
  }
};

export const updateLinksHrefNoLinksWarning = () => {
  const $links = document.querySelectorAll('a');

  const $linksWithNonTextNodes = Array.from($links).filter((link) => {
    return Array.from(link.childNodes).some((child) => child.nodeType !== Node.TEXT_NODE);
  });

  $linksWithNonTextNodes.forEach((link) => {
    const $linkRef = link.getAttribute('href') || '';

    $linkRef &&
      link.childNodes.forEach(($tag) => {
        $tag.nodeType === Node.ELEMENT_NODE && setNodeProperty($tag, 'href', $linkRef);
      });
  });
};

export function updateLinksHref() {
  const links = document.querySelectorAll('a');

  links.forEach((link) => {
    const linkRef = link.getAttribute('href') || '';
    const $isInternalLink = isRelativeLink(linkRef);
    const $isHeaderLink = isHeaderLink(link);

    if ($isHeaderLink) {
      link.setAttribute('data-exclude-type', 'header');
    } else if ($isInternalLink) {
      link.setAttribute('data-href', linkRef);
      link.setAttribute('href', 'javascript:');
      link.setAttribute('onclick', 'return false;');
    }
  });
}

export const replaceAll = (str: string, find: string, replace: string): string =>
  str.replace(new RegExp(find, 'g'), replace);

export const getCleanID = (id: string, defaultValue = ''): string => {
  id = id || defaultValue;
  id = replaceAll(id, '-', '').replace('{', '').replace('}', '');

  return isValidGUID(id, true) ? id : '';
};

export const isValidGUID = (strGUID: string, cleaned = false): boolean => {
  if (cleaned) {
    return strGUID ? strGUID.length === 32 && !(strGUID.indexOf(' ') >= 0) : false;
  } else {
    return strGUID ? strGUID.length === 38 && strGUID.startsWith('{') && strGUID.endsWith('}') : false;
  }
};

export const isValidSitecoreItemGUID = (value: string): boolean => {
  // Regular expression pattern to match Sitecore item GUID
  const regex = /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$|^[0-9a-f]{32}$/i;
  return regex.test(value);
};

export const parseJSON = (jsonString: string) => {
  try {
    return JSON.parse(jsonString);
  } catch (error) {
    console.log(error);
  }

  return null;
};

export const setSessionVariable = (key: string, value: string) => {
  if (typeof window !== 'undefined') {
    localStorage.setItem(key, value);
  }
};

export const getSessionVariable = (key: string): string => {
  let sessionValue = '';

  if (typeof window !== 'undefined') {
    sessionValue = localStorage.getItem(key) || '';
  }

  return sessionValue;
};

export const saveQueryStrings = (router: NextRouter, queryStringKey: string) => {
  setSessionVariable(queryStringKey, JSON.stringify(router.query));
};

export const getSavedPathWithQueryStrings = (queryStringKey: string): string => {
  let savedPathWithQueryStrings = getSessionVariable(queryStringKey) || '';

  if (savedPathWithQueryStrings) {
    const jsonObject = parseJSON(savedPathWithQueryStrings);
    savedPathWithQueryStrings = '';
    let relativePath = '';

    if (jsonObject) {
      const pathArray = jsonObject['path'];

      Array.isArray(pathArray) &&
        pathArray.map((pathPart: string) => {
          if (pathPart && !pathPart.startsWith('_site_')) {
            relativePath = `${relativePath}/${pathPart}`;
          }
        });

      Object.keys(jsonObject).forEach((key) => {
        if (key !== 'path') {
          const qs = `${key}=${jsonObject[key]}`;
          savedPathWithQueryStrings = `${savedPathWithQueryStrings}${savedPathWithQueryStrings ? '&' : '?'}${qs}`;
        }
      });

      if (savedPathWithQueryStrings) {
        savedPathWithQueryStrings = `${relativePath}${savedPathWithQueryStrings}`;
      }
    }
  }

  return savedPathWithQueryStrings;
};

export function getUrlWithoutExtension(url: string, extensions: string[]): string {
  let $newUrl = url;

  url &&
    extensions &&
    extensions.forEach((extension) => {
      if ($newUrl.endsWith(extension)) {
        $newUrl = $newUrl.replace(extension, '');
      }
    });

  return $newUrl === url ? '' : $newUrl;
}

export const getEffectiveMonthandYear = (date: string): { year: number; month: string } => {
  date = date.replaceAll('-', '').replaceAll(':', ''); //standardize the date format

  const monthAbbreviation = [
    'Jan.',
    'Feb.',
    'Mar.',
    'Apr.',
    'May',
    'Jun.',
    'Jul.',
    'Aug.',
    'Sep.',
    'Oct.',
    'Nov.',
    'Dec.',
  ];
  const effectiveMonthAbbr = monthAbbreviation[parseInt(date.substring(4, 6), 10) - 1];
  const effectiveYear = parseInt(date.substring(0, 4), 10);

  const $returnData = { year: effectiveYear, month: effectiveMonthAbbr };
  console.log($returnData, date);

  return $returnData;
};

/**
 *
 * @param {Date} date Date to convert
 * @returns date and time set to UTC
 */
export function getUTCdate(date: Date) {
  const convertDate = new Date(date);
  return new Date(
    Date.UTC(
      convertDate.getFullYear(),
      convertDate.getMonth(),
      convertDate.getDate(),
      convertDate.getHours(),
      convertDate.getMinutes(),
      convertDate.getSeconds()
    )
  );
}

/**
 *
 * @param {url} url string to get the HostURL - e.g. http://cm/default/page
 * @returns string - host url e.g. http://cm
 */
export const getHostURL = (url: string | undefined): string => {
  let $origin = '';

  if (url) {
    try {
      const $url = new URL(url);
      $origin = $url.origin;
    } catch {}
  }

  return $origin;
};

export const getShortLanguageCode = (languageCode: string, transformToUpper = true): string => {
  if (!languageCode) {
    return '';
  }

  if (languageCode.length > 2) {
    languageCode = languageCode.substring(0, 2);
  }

  languageCode = transformToUpper ? languageCode.toLocaleUpperCase() : languageCode;

  return languageCode;
};

export const getLanguageBasedUrl = (url: string, currentLanguage = ''): string => {
  const defaultLanguage = 'en';
  const websiteRootUrl = '/UPMCHealthPlan/UPMCHealthPlanWebsite/Home/';
  url = url.replace(websiteRootUrl, '/');

  if (!currentLanguage || currentLanguage === defaultLanguage) {
    return url.replace(`/${defaultLanguage}/`, '/');
  }

  if (url.startsWith(`/`) && !url.startsWith(`/${currentLanguage}/`)) {
    url = `/${currentLanguage}${url}`;
  }

  return url;
};

export const fixMediaImagePath = (image: ImageField) => {
  const imageSource = image.value?.src;

  const $mediaPrefix = '/-/media/';
  const parts = imageSource?.split($mediaPrefix);

  if (image.value && parts?.length == 2) {
    image.value.src = `${$mediaPrefix}${parts[1]}`;
  }

  return image;
};

export const getIdsFromArray = (filterArray: { id: string }[]) => {
  if (!filterArray?.length) {
    return '';
  }

  const ids = filterArray.map((a) => a.id).join(',');

  return ids;
};

export function chunkArray<T>(array: T[], size: number): T[][] {
  const result: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    if (array) {
      result.push(array.slice(i, i + size));
    }
  }

  return result;
}

export const formatDate = (locale: string | undefined, dateString: string) => {
  locale = locale || 'en';

  const date = new Date(dateString);

  const formattedDate = new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC', // Ensure it doesn't shift due to local time zone
  }).format(date);

  return formattedDate;
};

export const cleanHtmlTags = (input: string): string =>
  removeLineBreaks(input ? input.replace(/<\/?[^>]+(>|$)/g, '') : input);

export function encryptDecrypt(passedString: string, encrypt: boolean) {
  let returnString = '';

  if (passedString != undefined && passedString != null && passedString != '') {
    const secret_key = process.env['NEXT_PUBLIC_PLAN_FILTER_KEY'] || '';
    const iv = 'f1f0036b85fffe43';
    const algorithm = 'aes-256-cbc';

    try {
      if (encrypt) {
        const cipher = crypto.createCipheriv(algorithm, secret_key, iv);
        returnString = cipher.update(passedString, 'utf-8', 'hex');
        returnString += cipher.final('hex');
      } else {
        const decipher = crypto.createDecipheriv(algorithm, secret_key, iv);
        returnString = decipher.update(passedString, 'hex', 'utf-8');
        returnString += decipher.final('utf-8');
      }
    } catch (error) {
      console.log('Error handling plan filters');
      console.log(error);
    }
  }

  return returnString;
}

/**
 * Simple, reuseable function to add keyboard accesibility to existing links, buttons, etc.
 * @param e Keyboard Event
 * @param functionToRun The function to be called on a succeful click
 * @param acceptEnter Return key starts function, true by default
 * @param acceptSpace Spacebar returns functions, true by default
 * @param preventDefault Prevent default keyboard response(scrolling), true by default
 */
export const keyboardClick = (
  e: React.KeyboardEvent,
  functionToRun: () => void | React.FunctionComponent,
  acceptEnter = true,
  acceptSpace = true,
  preventDefault = true
) => {
  if ((acceptEnter && e.key == 'Enter') || (acceptSpace && e.key == ' ')) {
    if (preventDefault) {
      e.preventDefault();
    }
    functionToRun();
  }
};

export function isNonRelativeURL(url: string) {
  return /^[a-z][a-z0-9+.-]*:/i.test(url) || url.startsWith('//');
}

/**
 * Ensures the entire word is match, not just part of it
 * Example if 'cat' is passed, it will now only match cat and not cathedral
 * @param wordToMatch word to match completely
 * @returns regex escaped word
 */
export function MatchWholeWord(wordToMatch: string) {
  const escapedWord = wordToMatch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`\\b${escapedWord}\\b`, 'g');
}

export async function TokenReplace() {
  return;
}

/**
 * Fixes links from the rich text editor by adding necessary attributes and screen reader text.
 * @param text The HTML content from the rich text editor
 * @param opensInNewWindowText The text to be added for screen readers for links that open in a new window
 * @returns The modified HTML content
 */
export function fixLinksFromRichTextEditor(text: string, opensInNewWindowText: string) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(text, 'text/html');

  //#region Evolve URL rewrite
  const evolveUrls = doc.getElementsByName('evolveURL');
  evolveUrls.forEach((url) => {
    url.setAttribute(
      'href',
      buildEvolveURL(
        url.getAttribute('data-prefix') ?? 'UPMC',
        url.getAttribute('data-planID') ?? undefined,
        url.getAttribute('data-zip') ?? undefined,
        url.getAttribute('data-county') ?? undefined,
        url.getAttribute('data-year') ?? undefined,
        url.getAttribute('data-cta') ?? ''
      )
    );
    url.removeAttribute('name');
    url.removeAttribute('data-prefix');
    url.removeAttribute('data-planID');
    url.removeAttribute('data-zip');
    url.removeAttribute('data-county');
    url.removeAttribute('data-year');
    url.removeAttribute('data-cta');
  });
  //#endregion

  // #region Add screen reader text to blank anchors
  const blankAnchors = doc.querySelectorAll('a[target="_blank"]');
  blankAnchors.forEach((anchor) => {
    const srOnlySpan = doc.createElement('span');
    srOnlySpan.className = 'screen-reader-only';
    srOnlySpan.textContent = opensInNewWindowText;
    anchor.appendChild(srOnlySpan);
  });
  // #endregion

  return doc.body.innerHTML;
}

export const getAttributeValueFromJsonString = (jsonString: string, attributeName: string): string => {
  if (!jsonString || !attributeName) {
    return '';
  }

  let returnValue = jsonString;

  try {
    const jsonObject = JSON.parse(decodeURI(jsonString));
    returnValue = jsonObject[attributeName] || jsonString;
  } catch (error) {
    console.warn(error);
  }

  return returnValue;
};

export const GetPiwikSession = () => {
  let piwikSession = localStorage.getItem('piwikSession') ?? undefined;

  if (!piwikSession) {
    const allCookies = Cookies.get();

    const piwikSessionCookie = Object.keys(allCookies).filter(
      (x) => x.startsWith('_pk_id') && x.indexOf(process.env['NEXT_PUBLIC_PIWIK_CONTAINER_ID'] ?? ' ') > 0
    );
    piwikSessionCookie.forEach((cookie) => {
      if (cookie.startsWith('_pk_id.')) {
        const pkSessionID = Cookies.get(cookie);

        if (pkSessionID) {
          const pkSessionSplit = pkSessionID?.split('.');
          piwikSession = pkSessionSplit[0] + '.' + pkSessionSplit[2];
          localStorage.setItem('piwikSession', piwikSession);
        }
      }
    });
  }
  return piwikSession;
};

export const getCookieValue = (cookieName: string) => {
  if (!cookieName || !document.cookie) {
    return '';
  }

  const allCookies = decodeURIComponent(document.cookie).split(';');
  const cookie = allCookies.find((c) => c.trim().startsWith(cookieName + '='));

  if (cookie) {
    const cookieParts = cookie.split('=');
    return cookieParts[1].trim();
  }

  return '';
};

export const pageHasComponent = (
  placeholders: PlaceholdersData<string> | undefined,
  componentName: string
): boolean => {
  if (!placeholders) {
    return false;
  }

  for (const key of Object.keys(placeholders)) {
    const items = placeholders[key] as Array<ComponentRendering>;

    if (!Array.isArray(items)) {
      continue;
    }

    for (const item of items) {
      if (item.componentName === componentName) {
        return true;
      }

      if (item.placeholders) {
        if (pageHasComponent(item.placeholders, componentName)) {
          return true;
        }
      }
    }
  }

  return false;
};
